import { handleActions } from 'redux-actions';
import { Actions, deviceConfigDeleted, deviceConfigsFailure, deviceConfigsReload, deviceConfigsRequest, deviceConfigsSuccess } from 'actions/deviceConfigActions';
import { Actions as SessionActions } from 'actions/sessionActions';
import { UtcTime, DeviceConfig, DeviceConfigId, BuildingId } from 'shared/models';
import { omit, uniq } from 'lodash';

export type State = Readonly<{
    data: Record<DeviceConfigId, DeviceConfig>;
    byBuilding: Record<BuildingId, DeviceConfigId[]>;
    loading: boolean;
    errorKey: string | null;
    nextFetch: Record<BuildingId, UtcTime>;
}>;

export const initialState = Object.freeze<State>({
    data: {},
    byBuilding: {},
    loading: false,
    errorKey: null,
    nextFetch: {},
});

export const deviceConfigsReducer = handleActions({
    [Actions.DEVICE_CONFIGS_REQUEST]: (state: State, action: ReturnType<typeof deviceConfigsRequest>) => {
        const buildingId = action.payload;
        const { nextFetch } = action.meta;
        return {
            ...state,
            loading: true,
            nextFetch: {
                ...state.nextFetch,
                [buildingId]: nextFetch,
            },
        };
    },

    [Actions.DEVICE_CONFIGS_SUCCESS]: (state: State, action: ReturnType<typeof deviceConfigsSuccess>) => {
        const { buildingId } = action.meta;
        
        const data = action.payload.reduce((obj, dc) => {
            obj[dc.id] = dc;
            return obj;
        }, { ...state.data });

        const byBuilding = {
            ...state.byBuilding,
            [buildingId]: uniq(action.payload.reduce((array, dc) => {
                array.push(dc.id);
                return array;
            }, [ ...(state.byBuilding[buildingId] || []) ])),
        };

        return {
            ...state,
            data,
            byBuilding,
            errorKey: null,
            loading: false,
        };
    },

    [Actions.DEVICE_CONFIGS_FAILURE]: (state: State, action: ReturnType<typeof deviceConfigsFailure>) => {
        return {
            ...state,
            errorKey: action.payload.message,
            loading: false,
        };
    },

    [Actions.DEVICE_CONFIG_DELETED]: (state: State, action: ReturnType<typeof deviceConfigDeleted>) => {
        const deviceConfigId = action.payload;
        const { data, byBuilding } = state;
        const deviceConfig = data[deviceConfigId];
        if (!deviceConfig) {
            return state;
        }
        const array = byBuilding[deviceConfig.buildingId];
        return {
            ...state,
            data: omit(data, deviceConfigId),
            byBuilding: {
                ...byBuilding,
                [deviceConfig.buildingId]: array.filter(id => id !== deviceConfigId),
            },
        };
    },

    [Actions.DEVICE_CONFIGS_RELOAD]: (state: State, action: ReturnType<typeof deviceConfigsReload>) => {
        const buildingId = action.payload;
        const { byBuilding, nextFetch } = state;
        return {
            ...state,
            byBuilding: omit(byBuilding, buildingId),
            nextFetch: {
                ...nextFetch,
                [buildingId]: 0,
            },
        };
    },


    //
    //  Session Actions
    //
    [SessionActions.LOGOUT_SUCCESS]: () => {
        return initialState;
    },
} as any, initialState);
